import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Clock, CornerDownLeft, Laptop, FolderPlus, FolderUp, Globe, HardDrive, Moon, Search, Star, Trash2, Upload, UserCog, Users,
} from 'lucide-react';
import driveApi from '../../api/drive';
import { useAuth } from '../../contexts/AuthProvider';
import { useDrive } from '../../contexts/DriveProvider';
import { FileTag } from './ItemsView';
import { DRIVE_EVENTS } from './events';

const toggleTheme = () => {
  const dark = !document.documentElement.classList.contains('dark');
  document.documentElement.classList.toggle('dark', dark);
  try { localStorage.setItem('theme', dark ? 'dark' : 'light'); } catch { /* storage blocked */ }
};

/** ⌘K: jump to any page, drive, file or folder, or run an action — without the mouse. */
const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { personalDrive, sharedDrives } = useDrive();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(() => {
      driveApi.search(q).then((r) => setResults(r.slice(0, 8))).catch(() => setResults([]));
    }, 160);
    return () => clearTimeout(t);
  }, [query]);

  const commands = useMemo(() => {
    const go = (path) => () => navigate(path);
    const fire = (name) => () => setTimeout(() => window.dispatchEvent(new Event(name)), 50);
    const list = [
      { group: 'Actions', label: 'New folder', icon: FolderPlus, run: fire(DRIVE_EVENTS.newFolder) },
      { group: 'Actions', label: 'Upload files', icon: Upload, run: fire(DRIVE_EVENTS.uploadFiles) },
      { group: 'Actions', label: 'Upload folder', icon: FolderUp, run: fire(DRIVE_EVENTS.uploadFolder) },
      { group: 'Actions', label: 'Switch light / dark mode', icon: Moon, run: toggleTheme },
    ];
    if (personalDrive) list.push({ group: 'Go to', label: 'My Drive', icon: HardDrive, run: go(`/drive/${personalDrive.id}`) });
    list.push(
      { group: 'Go to', label: 'Shared with me', icon: Users, run: go('/shared') },
      { group: 'Go to', label: 'Recent', icon: Clock, run: go('/recent') },
      { group: 'Go to', label: 'Starred', icon: Star, run: go('/starred') },
      { group: 'Go to', label: 'External links', icon: Globe, run: go('/links') },
      { group: 'Go to', label: 'Trash', icon: Trash2, run: go('/trash') },
      { group: 'Go to', label: 'Desktop app', icon: Laptop, run: go('/desktop') },
    );
    sharedDrives.forEach((d) => list.push({ group: 'Go to', label: d.name, swatch: d.color, run: go(`/drive/${d.id}`) }));
    if (user?.is_staff) list.push({ group: 'Go to', label: 'People & teams', icon: UserCog, run: go('/admin/people') });
    return list;
  }, [navigate, personalDrive, sharedDrives, user?.is_staff]);

  const q = query.trim().toLowerCase();
  const matching = commands.filter((c) => !q || c.label.toLowerCase().includes(q));
  const fileEntries = results.map((item) => ({
    group: 'Files and folders',
    label: item.name,
    hint: item.parent_name ? `${item.drive_name} / ${item.parent_name}` : item.drive_name,
    item,
    run: () => navigate(item.kind === 'folder' ? `/drive/${item.drive}/folder/${item.id}` : (item.parent ? `/drive/${item.drive}/folder/${item.parent}` : `/drive/${item.drive}`)),
  }));
  const searchAll = q.length >= 2
    ? [{ group: 'Files and folders', label: `See all results for “${query.trim()}”`, icon: Search, run: () => navigate(`/search?q=${encodeURIComponent(query.trim())}`) }]
    : [];
  const entries = [...fileEntries, ...searchAll, ...matching];

  useEffect(() => { setActive(0); }, [query, results.length]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (entry) => {
    onOpenChange(false);
    entry.run();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, entries.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter' && entries[active]) { e.preventDefault(); choose(entries[active]); }
  };

  let lastGroup = null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-label="Search and commands"
          className="fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-[600px] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98]"
        >
          <DialogPrimitive.Title className="sr-only">Search and commands</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Type to search files, folders and actions. Use the arrow keys and Enter.</DialogPrimitive.Description>
          <div className="flex items-center gap-2.5 border-b border-border px-4">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search files, folders and actions…"
              aria-label="Search files, folders and actions"
              role="combobox"
              aria-expanded="true"
              aria-controls="palette-list"
              aria-activedescendant={entries[active] ? `palette-${active}` : undefined}
              className="h-12 flex-1 bg-transparent text-[15px] outline-none placeholder:text-muted-foreground"
            />
            <kbd className="rounded border border-border px-1.5 text-[11px] text-muted-foreground">esc</kbd>
          </div>
          <ul id="palette-list" ref={listRef} role="listbox" className="max-h-[min(60vh,420px)] overflow-y-auto p-1.5">
            {entries.length === 0 && (
              <li className="px-3 py-8 text-center text-sm text-muted-foreground">No matches. Try another name.</li>
            )}
            {entries.map((entry, i) => {
              const header = entry.group !== lastGroup ? entry.group : null;
              lastGroup = entry.group;
              const Icon = entry.icon;
              return (
                <React.Fragment key={`${entry.group}-${entry.label}-${i}`}>
                  {header && <li role="presentation" className="px-2.5 pb-1 pt-2.5 text-xs font-medium text-muted-foreground">{header}</li>}
                  <li
                    id={`palette-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={i === active}
                    onMouseMove={() => setActive(i)}
                    onClick={() => choose(entry)}
                    className={`flex h-10 cursor-pointer items-center gap-3 rounded-md px-2.5 text-[13.5px] ${i === active ? 'bg-muted' : ''}`}
                  >
                    {entry.item ? <span className="scale-[0.85]"><FileTag item={entry.item} /></span>
                      : entry.swatch ? <span className="mx-[3px] h-2.5 w-2.5 rounded-[3px]" style={{ background: entry.swatch }} aria-hidden="true" />
                        : Icon && <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                    <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                    {entry.hint && <span className="truncate text-xs text-muted-foreground">{entry.hint}</span>}
                    {i === active && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default CommandPalette;
